import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import React from 'react';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { StudyPageNavigationQuery } from '~/apollo/generated/v4/graphql';
import type { NavLinkProps } from '~/components/common/NavLink';
import { NavLink } from '~/components/common/NavLink';
import { Tooltip } from '~/components/common/Tooltip';
import { Badge } from '~/components/ui/badge';
import { Menu } from '~/components/ui/menu';
import * as routes from '~/paths';

const STUDY_PAGE_NAVIGATION = graphql(`
  query StudyPageNavigation($id: ID!) {
    studyGet(id: $id) {
      id
      pictures {
        id
      }
      crossSections {
        id
      }
      facies {
        id
      }
      sedimentaryLogs {
        id
      }
      wellLogs {
        id
      }
      syntheticWellLogs {
        id
      }
      reservoirModels {
        id
      }
      production {
        id
      }
      trainingImages {
        id
      }
      variograms {
        id
      }
      gigaPans {
        id
      }
      architecturalElementCount
      measurementCount
    }
  }
`);

function CountBadge({
  children,
  count,
}: {
  children?: React.ReactNode;
  count: number;
}) {
  return (
    <Badge color="ghost" className="break-words">
      {children ? children : count}
    </Badge>
  );
}

function StudyNavLink(props: NavLinkProps) {
  return (
    <NavLink {...props} className="flex flex-wrap items-start gap-2">
      {props.children}
    </NavLink>
  );
}

type Study = NonNullable<StudyPageNavigationQuery['studyGet']>;

type CountableStudyField = keyof Omit<
  Study,
  '__typename' | 'id' | 'architecturalElementCount' | 'measurementCount'
>;

function itemCount<T extends CountableStudyField>(items: Study[T] | undefined) {
  return items?.length ?? 0;
}
function hasItems<T extends CountableStudyField>(items: Study[T] | undefined) {
  return !!items && itemCount(items) > 0;
}

type Props = {
  studyId: number;
};
export function StudyRouteNavigation(props: Props) {
  return (
    <ApolloProviderV4>
      <StudyRouteNavigationInner {...props} />
    </ApolloProviderV4>
  );
}

function StudyRouteNavigationInner({ studyId }: Props) {
  const { data } = useQuery(STUDY_PAGE_NAVIGATION, {
    variables: { id: studyId },
  });

  const study = data?.studyGet;

  const aeCount = study?.architecturalElementCount ?? 0;
  const measurementCount = study?.measurementCount ?? 0;

  const hasAnySOs = R.any(hasItems, [
    study?.pictures,
    study?.facies,
    study?.crossSections,
    study?.sedimentaryLogs,
    study?.wellLogs,
    study?.production,
    study?.reservoirModels,
    study?.trainingImages,
    study?.variograms,
    study?.gigaPans,
  ]);

  return (
    <Menu>
      <Menu.Item>
        <StudyNavLink to={routes.studySummaryRoute(studyId)} end>
          Summary
        </StudyNavLink>
      </Menu.Item>
      <Menu.Item>
        <StudyNavLink to={routes.studyKeyParametersRoute(studyId)}>
          Key Parameters
        </StudyNavLink>
      </Menu.Item>

      {measurementCount > 0 && (
        <>
          <li />
          <Menu.Item>
            <StudyNavLink to={routes.studyMeasurementsRoute(studyId)}>
              Measurements
              <Tooltip
                message={`${measurementCount} measurements on ${aeCount} architectural elements`}
              >
                <span>
                  <CountBadge count={measurementCount}>
                    {measurementCount} / {aeCount} AEs
                  </CountBadge>
                </span>
              </Tooltip>
            </StudyNavLink>
          </Menu.Item>
        </>
      )}

      {hasAnySOs && <li />}

      {hasItems(study?.pictures) && (
        <Menu.Item>
          <StudyNavLink to={routes.studyPicturesTabRoute(studyId)}>
            Pictures
            <CountBadge count={itemCount(study?.pictures)} />
          </StudyNavLink>
        </Menu.Item>
      )}
      {hasItems(study?.facies) && (
        <Menu.Item>
          <StudyNavLink to={routes.studyFaciesTabRoute(studyId)}>
            Facies
            <CountBadge count={itemCount(study?.facies)} />
          </StudyNavLink>
        </Menu.Item>
      )}
      {hasItems(study?.crossSections) && (
        <Menu.Item>
          <StudyNavLink to={routes.studyCrossSectionsTabRoute(studyId)}>
            Cross Sections
            <CountBadge count={itemCount(study?.crossSections)} />
          </StudyNavLink>
        </Menu.Item>
      )}
      {hasItems(study?.sedimentaryLogs) && (
        <Menu.Item>
          <StudyNavLink to={routes.studySedimentaryLogsTabRoute(studyId)}>
            Sedimentary Logs
            <CountBadge count={itemCount(study?.sedimentaryLogs)} />
          </StudyNavLink>
        </Menu.Item>
      )}
      {hasItems(study?.wellLogs) && (
        <Menu.Item>
          <StudyNavLink to={routes.studyWellLogsTabRoute(studyId)}>
            Well Logs
            <CountBadge count={itemCount(study?.wellLogs)} />
          </StudyNavLink>
        </Menu.Item>
      )}
      {hasItems(study?.syntheticWellLogs) && (
        <Menu.Item>
          <StudyNavLink to={routes.studySyntheticWellLogsTabRoute(studyId)}>
            Synthetic Well Logs
            <CountBadge count={itemCount(study?.syntheticWellLogs)} />
          </StudyNavLink>
        </Menu.Item>
      )}
      {hasItems(study?.production) && (
        <Menu.Item>
          <StudyNavLink to={routes.studyProductionTabRoute(studyId)}>
            Production
            <CountBadge count={itemCount(study?.production)} />
          </StudyNavLink>
        </Menu.Item>
      )}
      {hasItems(study?.reservoirModels) && (
        <Menu.Item>
          <StudyNavLink to={routes.studyReservoirModelsTabRoute(studyId)}>
            Reservoir Models
            <CountBadge count={itemCount(study?.reservoirModels)} />
          </StudyNavLink>
        </Menu.Item>
      )}
      {hasItems(study?.trainingImages) && (
        <Menu.Item>
          <StudyNavLink to={routes.studyTrainingImagesTabRoute(studyId)}>
            Training Images
            <CountBadge count={itemCount(study?.trainingImages)} />
          </StudyNavLink>
        </Menu.Item>
      )}
      {hasItems(study?.variograms) && (
        <Menu.Item>
          <StudyNavLink to={routes.studyVariogramsTabRoute(studyId)}>
            Variograms
            <CountBadge count={itemCount(study?.variograms)} />
          </StudyNavLink>
        </Menu.Item>
      )}
      {hasItems(study?.gigaPans) && (
        <Menu.Item>
          <StudyNavLink to={routes.studyPanoramasTabRoute(studyId)}>
            Panoramas
            <CountBadge count={itemCount(study?.gigaPans)} />
          </StudyNavLink>
        </Menu.Item>
      )}
    </Menu>
  );
}
